<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => [],
  },
  collectionFields: {
    type: Array,
    default: () => [],
  },
  qualificationLists: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue']);

const rules = computed({
  get: () => props.modelValue || [],
  set: val => emit('update:modelValue', val),
});

const addRule = () => {
  rules.value = [
    ...rules.value,
    {
      campo_avaliado_id: props.collectionFields[0]?.id || null,
      lista_id: props.qualificationLists[0]?.id || null,
    },
  ];
};

const removeRule = idx => {
  const r = [...rules.value];
  const item = r[idx];
  if (item.id) {
    item._destroy = true;
  } else {
    r.splice(idx, 1);
  }
  rules.value = r;
};

const visibleRules = computed(() => {
  return rules.value.filter(r => !r._destroy);
});
</script>

<template>
  <div class="space-y-4 max-w-4xl">
    <div class="bg-n-surface-1 border border-n-weak rounded-xl p-6 space-y-4">
      <div class="flex items-center justify-between">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            Regras de Cruzamento & Qualificação
          </h3>
          <p class="text-xs text-n-slate-11">
            Indique qual campo de coleta será avaliado contra qual lista de
            qualificação.
          </p>
        </div>
        <Button
          sm
          icon="i-lucide-plus"
          label="Adicionar Regra"
          @click="addRule"
        />
      </div>

      <div
        v-if="visibleRules.length === 0"
        class="text-center py-8 border border-dashed border-n-weak rounded-lg text-n-slate-11 text-sm"
      >
        Nenhuma regra configurada. Clique em "+ Adicionar Regra" para criar.
      </div>

      <div v-else class="space-y-3">
        <div
          v-for="(rule, idx) in visibleRules"
          :key="rule.id || idx"
          class="flex items-center gap-4 bg-n-surface-2 p-4 rounded-xl border border-n-weak hover:border-n-brand/50 transition-colors"
        >
          <div
            class="flex items-center gap-2 text-xs font-semibold text-n-slate-12 flex-1"
          >
            <span>Avaliar campo</span>
            <select
              v-model="rule.campo_avaliado_id"
              class="reset-base !mb-0 flex-1 text-xs font-mono bg-n-surface-1 border border-n-weak rounded-lg px-2.5 py-1.5 text-n-slate-12 focus:border-n-brand outline-none"
            >
              <option :value="null">Selecione o Campo Coletado</option>
              <option
                v-for="field in collectionFields.filter(f => !f._destroy)"
                :key="field.id || field.nome_interno"
                :value="field.id"
              >
                {{ field.nome_interno }} ({{ field.pergunta }})
              </option>
            </select>

            <span class="px-2">contra a lista</span>

            <select
              v-model="rule.lista_id"
              class="reset-base !mb-0 flex-1 text-xs font-mono bg-n-surface-1 border border-n-weak rounded-lg px-2.5 py-1.5 text-n-slate-12 focus:border-n-brand outline-none"
            >
              <option :value="null">Selecione a Lista de Qualificação</option>
              <option
                v-for="list in qualificationLists.filter(l => !l._destroy)"
                :key="list.id || list.nome"
                :value="list.id"
              >
                {{ list.nome }}
              </option>
            </select>
          </div>

          <Button
            ghost
            ruby
            sm
            icon="i-lucide-trash-2"
            @click="removeRule(idx)"
          />
        </div>
      </div>
    </div>
  </div>
</template>
